import { Box, CircularProgress, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import DownloadIcon from "../../../../assets/icons/DownloadIcon.svg";
import PingIcon from "../../../../assets/icons/PingIcon.svg";
import UploadIcon from "../../../../assets/icons/UploadIcon.svg";

interface SpeedDialGaugeProps {
  speed: number;
  maxSpeed?: number;
  size?: number;
  mode?: "download" | "upload" | "ping" | "idle";
  unit?: string;
  loading?: boolean;
  statusText?: string;
  statusColor?: string;
  isError?: boolean;
  isResultReady?: boolean;
  onAnimationComplete?: (mode: "download" | "upload" | "ping") => void;
}

const SpeedDialGauge: React.FC<SpeedDialGaugeProps> = ({
  speed = 0,
  maxSpeed = 1000,
  size = 220,
  mode = "idle",
  unit = "Mbps",
  loading = false,
  statusText,
  statusColor,
  isError = false,
  isResultReady = true,
  onAnimationComplete,
}) => {
  const [displayCount, setDisplayCount] = useState(0);
  const [animSweep, setAnimSweep] = useState(0); // 0 to SWEEP degrees
  const [awaitingAnimationCompletion, setAwaitingAnimationCompletion] = useState(false);
  const rafRef = useRef<number | null>(null);
  const currentSweepRef = useRef(0);
  const currentDisplayRef = useRef(0);
  const shouldNotifyCompleteRef = useRef(false);
  const onAnimationCompleteRef = useRef(onAnimationComplete);

  useEffect(() => {
    onAnimationCompleteRef.current = onAnimationComplete;
  }, [onAnimationComplete]);

  const cx = size / 2;
  const cy = size / 2;
  const R = size / 2;

  // Keep the whole ring inside bounds to avoid polygon-like clipping at corners.
  const ringStroke = R * 0.2;
  const R_outer = R - ringStroke / 2 - 0.2;
  const ringInnerRadius = R_outer - ringStroke / 2;
  const bandWidth = R * 0.34;
  const innerFaceRadius = ringInnerRadius - bandWidth;
  const arcRadius = innerFaceRadius - R * 0.001;
  const arcStroke = R * 0.06;
  const labelRadius = ringInnerRadius - bandWidth / 2;
  const tickOuterRadius = ringInnerRadius - bandWidth / 2 - R * 0.02;
  const tickInnerRadius = tickOuterRadius - R * 0.07;

  // Arc geometry: 270° sweep from bottom-left (225°) clockwise to bottom-right (135°)
  const START_ANGLE = 225;
  const SWEEP = 270;

  // Convert angle (0 = top, clockwise positive) to SVG cartesian
  const polarToXY = (r: number, deg: number) => {
    const rad = ((deg - 90) * Math.PI) / 180;
    return {
      x: +(cx + r * Math.cos(rad)).toFixed(3),
      y: +(cy + r * Math.sin(rad)).toFixed(3),
    };
  };

  // Build SVG arc path from startDeg sweeping sweepDeg clockwise
  const makeArc = (r: number, startDeg: number, sweepDeg: number): string => {
    if (sweepDeg <= 0) return "";
    const safeSwep = Math.min(sweepDeg, 359.99);
    const s = polarToXY(r, startDeg);
    const e = polarToXY(r, startDeg + safeSwep);
    const large = safeSwep > 180 ? 1 : 0;
    return `M ${s.x} ${s.y} A ${r} ${r} 0 ${large} 1 ${e.x} ${e.y}`;
  };

  // 7 evenly-spaced tick labels
  const numLabels = 7;
  const labelValues = Array.from({ length: numLabels }, (_, i) =>
    Math.round((maxSpeed / (numLabels - 1)) * i)
  );
  const divisions = 2;
  const tickStep = SWEEP / ((numLabels - 1) * divisions);
  const tickCount = (numLabels - 1) * divisions + 1;

  const getRating = (error: boolean) => {
    if (error) return { label: "Unstable" };
    return { label: "Excellent" };
  };

  useEffect(() => {
    if (loading && mode !== "idle") {
      shouldNotifyCompleteRef.current = true;
      setAwaitingAnimationCompletion(true);
    }
  }, [loading, mode]);

  // Animate toward new speed
  useEffect(() => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    const safeMaxSpeed = maxSpeed > 0 ? maxSpeed : 1;
    const targetDisplay = Math.max(Math.round(speed), 0);
    const fromDisplay = currentDisplayRef.current;
    const duration = 2500;
    const startTime = performance.now();

    const animate = (now: number) => {
      const t = Math.min((now - startTime) / duration, 1);
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      const currentDisplay = fromDisplay + (targetDisplay - fromDisplay) * eased;
      const roundedDisplay = Math.round(currentDisplay);
      const cappedDisplay = Math.min(Math.max(roundedDisplay, 0), safeMaxSpeed);
      const currentSweep = (cappedDisplay / safeMaxSpeed) * SWEEP;

      setAnimSweep(currentSweep);
      currentSweepRef.current = currentSweep;
      setDisplayCount(roundedDisplay);
      currentDisplayRef.current = currentDisplay;
      if (t < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        const finalCappedDisplay = Math.min(Math.max(targetDisplay, 0), safeMaxSpeed);
        const finalSweep = (finalCappedDisplay / safeMaxSpeed) * SWEEP;
        setAnimSweep(finalSweep);
        currentSweepRef.current = finalSweep;
        setDisplayCount(targetDisplay);
        currentDisplayRef.current = targetDisplay;
        if (
          shouldNotifyCompleteRef.current &&
          !loading &&
          mode !== "idle" &&
          onAnimationCompleteRef.current
        ) {
          shouldNotifyCompleteRef.current = false;
          setAwaitingAnimationCompletion(false);
          onAnimationCompleteRef.current(mode);
        }
      }
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [speed, maxSpeed, loading, mode]);

  const trackPath = makeArc(arcRadius, START_ANGLE, SWEEP);
  const progressPath = animSweep > 0.5 ? makeArc(arcRadius, START_ANGLE, animSweep) : null;
  const rating = getRating(isError);
  const resolvedStatus = statusText || `${rating.label}`;
  const phaseLabel = mode === "upload" ? "Upload" : mode === "download" ? "Download" : "Ping";
  const isDialLoading = loading || awaitingAnimationCompletion || !isResultReady;

  return (
    <Box position="relative" display="inline-block" width={size} height={size + size * 0.19}>
      <svg width={size} height={size}>
        <defs>
          {/* Subtle decorative sheen like Figma (avoid heavy triangle). */}
          <linearGradient id={`shade-${size}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0" />
            <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0.08" />
          </linearGradient>
          <filter
            id={`progress-inner-shadow-${size}`}
            x="-50%"
            y="-50%"
            width="200%"
            height="200%"
            filterUnits="objectBoundingBox">
            <feOffset dx="0" dy="0.43" />
            <feGaussianBlur stdDeviation="1.87" result="offset-blur" />
            <feComposite in="SourceGraphic" in2="offset-blur" operator="out" result="inverse" />
            <feFlood floodColor="#000000" floodOpacity="0.149" result="shadow-color" />
            <feComposite in="shadow-color" in2="inverse" operator="in" result="inner-shadow" />
            <feComposite in="SourceGraphic" in2="inner-shadow" operator="over" />
          </filter>
          <filter
            id={`progress-blur-${size}`}
            x="-50%"
            y="-50%"
            width="200%"
            height="200%"
            filterUnits="objectBoundingBox">
            <feGaussianBlur stdDeviation="8.689" />
          </filter>
          <filter
            id={`outer-ring-inner-shadow-${size}`}
            x="-50%"
            y="-50%"
            width="200%"
            height="200%"
            filterUnits="objectBoundingBox">
            <feOffset in="SourceAlpha" dx="0" dy="1.1" result="inner-off-dark" />
            <feGaussianBlur in="inner-off-dark" stdDeviation="1.8" result="inner-blur-dark" />
            <feComposite
              in="inner-blur-dark"
              in2="SourceAlpha"
              operator="out"
              result="inner-cut-dark"
            />
            <feFlood floodColor="#000000" floodOpacity="0.2" result="inner-color-dark" />
            <feComposite
              in="inner-color-dark"
              in2="inner-cut-dark"
              operator="in"
              result="inner-shadow-dark"
            />

            <feOffset in="SourceAlpha" dx="0" dy="-0.8" result="inner-off-light" />
            <feGaussianBlur in="inner-off-light" stdDeviation="1.2" result="inner-blur-light" />
            <feComposite
              in="inner-blur-light"
              in2="SourceAlpha"
              operator="out"
              result="inner-cut-light"
            />
            <feFlood floodColor="#FFFFFF" floodOpacity="0.18" result="inner-color-light" />
            <feComposite
              in="inner-color-light"
              in2="inner-cut-light"
              operator="in"
              result="inner-shadow-light"
            />

            <feComposite
              in="SourceGraphic"
              in2="inner-shadow-dark"
              operator="over"
              result="outer-base-dark"
            />
            <feComposite
              in="outer-base-dark"
              in2="inner-shadow-light"
              operator="over"
              result="outer-base"
            />
            <feDropShadow
              in="outer-base"
              dx="0"
              dy="1.2"
              stdDeviation="1.5"
              floodColor="#000000"
              floodOpacity="0.1"
              result="outer-ds-1"
            />
            <feDropShadow
              in="outer-ds-1"
              dx="0"
              dy="2.4"
              stdDeviation="2.4"
              floodColor="#000000"
              floodOpacity="0.06"
            />
          </filter>
          <filter
            id={`ring-face-inner-shadow-${size}`}
            x="-50%"
            y="-50%"
            width="200%"
            height="200%"
            filterUnits="objectBoundingBox">
            <feOffset in="SourceAlpha" dx="0" dy="0.8" result="inner-off-a" />
            <feGaussianBlur in="inner-off-a" stdDeviation="1.6" result="inner-blur-a" />
            <feComposite in="inner-blur-a" in2="SourceAlpha" operator="out" result="inner-cut-a" />
            <feFlood floodColor="#000000" floodOpacity="0.14" result="inner-color-a" />
            <feComposite
              in="inner-color-a"
              in2="inner-cut-a"
              operator="in"
              result="inner-shadow-a"
            />
            <feComposite
              in="SourceGraphic"
              in2="inner-shadow-a"
              operator="over"
              result="ring-base-a"
            />

            <feOffset in="SourceAlpha" dx="0" dy="-1.2" result="inner-off-b" />
            <feGaussianBlur in="inner-off-b" stdDeviation="2.2" result="inner-blur-b" />
            <feComposite in="inner-blur-b" in2="SourceAlpha" operator="out" result="inner-cut-b" />
            <feFlood floodColor="#000000" floodOpacity="0.1" result="inner-color-b" />
            <feComposite
              in="inner-color-b"
              in2="inner-cut-b"
              operator="in"
              result="inner-shadow-b"
            />
            <feComposite
              in="ring-base-a"
              in2="inner-shadow-b"
              operator="over"
              result="ring-base-b"
            />

            <feDropShadow
              in="ring-base-b"
              dx="0"
              dy="1"
              stdDeviation="1"
              floodColor="#000000"
              floodOpacity="0.06"
              result="ring-ds-1"
            />
            <feDropShadow
              in="ring-ds-1"
              dx="0"
              dy="2"
              stdDeviation="1.6"
              floodColor="#000000"
              floodOpacity="0.04"
            />
          </filter>
        </defs>

        {/* Outer cyan full circle */}
        <circle
          cx={cx}
          cy={cy}
          r={R_outer}
          stroke="rgba(2, 202, 255, 1)"
          strokeWidth={ringStroke}
          fill="none"
          filter={`url(#outer-ring-inner-shadow-${size})`}
        />

        {/* Ring and inner face */}
        <circle
          cx={cx}
          cy={cy}
          r={ringInnerRadius}
          fill="#026CE3"
          filter={`url(#ring-face-inner-shadow-${size})`}
        />
        <circle cx={cx} cy={cy} r={innerFaceRadius} fill="#026CE3" />

        {/* Soft sheen sector on lower-right edge */}
        <path
          d={`M ${cx} ${cy} L ${polarToXY(ringInnerRadius, 22).x} ${polarToXY(ringInnerRadius, 22).y} A ${ringInnerRadius} ${ringInnerRadius} 0 0 1 ${polarToXY(ringInnerRadius, 52).x} ${polarToXY(ringInnerRadius, 52).y} Z`}
          fill={`url(#shade-${size})`}
        />

        {/* Track arc background */}
        <path
          d={trackPath}
          fill="none"
          stroke="white"
          strokeOpacity="0.9"
          strokeWidth={arcStroke}
          strokeLinecap="round"
        />

        {/* Progress arc (cyan) */}
        {progressPath && (
          <>
            <path
              d={progressPath}
              fill="none"
              stroke="#72D4F8"
              strokeOpacity="0.55"
              strokeWidth={arcStroke}
              strokeLinecap="round"
              filter={`url(#progress-blur-${size})`}
            />
            <path
              d={progressPath}
              fill="none"
              stroke="#72D4F8"
              strokeWidth={arcStroke}
              strokeLinecap="round"
              filter={`url(#progress-inner-shadow-${size})`}
            />
          </>
        )}

        {/* Tick marks between major labels */}
        {Array.from({ length: tickCount }, (_, i) => {
          const deg = START_ANGLE + i * tickStep;
          const isMajor = i % divisions === 0;
          const inner = polarToXY(tickInnerRadius, deg);
          const outer = polarToXY(tickOuterRadius, deg);
          if (isMajor) return null;
          return (
            <line
              key={`tick-${i}`}
              x1={inner.x}
              y1={inner.y}
              x2={outer.x}
              y2={outer.y}
              stroke="#FFFFFF"
              strokeWidth={1.5}
              strokeLinecap="round"
            />
          );
        })}

        {/* Speed labels around the arc */}
        {labelValues.map((val, i) => {
          const deg = START_ANGLE + (i / (numLabels - 1)) * SWEEP;
          const pos = polarToXY(labelRadius, deg);
          return (
            <text
              key={i}
              x={pos.x}
              y={pos.y + 4}
              textAnchor="middle"
              fill="#fff"
              fontSize={10}
              fontFamily="inherit"
              opacity={0.95}>
              {val}
            </text>
          );
        })}
      </svg>

      {/* Center overlay: mode label, speed value, unit */}
      {
        <Box
          position="absolute"
          top="44%"
          left="50%"
          sx={{
            transform: "translate(-50%, -48%)",
            textAlign: "center",
            pointerEvents: "none",
            width: innerFaceRadius * 2,
          }}>
          <Typography color="white" fontWeight={500} lineHeight={1.3} fontSize={12}>
            <img
              src={mode === "download" ? DownloadIcon : mode === "upload" ? UploadIcon : PingIcon}
              alt=""
            />{" "}
            {phaseLabel}
          </Typography>

          <Typography
            color="white"
            fontWeight={700}
            lineHeight={1}
            fontSize={phaseLabel !== "Ping" ? 36 : 24}
            letterSpacing={-1}>
            {displayCount}
          </Typography>

          <Typography color="white" fontWeight={500} fontSize={12}>
            {unit}
          </Typography>
        </Box>
      }

      {/* Attached loading/status pill */}
      <Box
        position="absolute"
        left="50%"
        bottom={0}
        sx={{
          transform: "translate(-50%, 0)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          width: size * 0.62,
          minHeight: size * 0.16,
          borderRadius: 999,
          px: 2,
          py: 1.1,
          backgroundColor: !isDialLoading && isError ? "#FF1400" : "#009424",
        }}>
        {isDialLoading ? (
          <>
            <CircularProgress size={16} thickness={6} sx={{ color: "#fff" }} />
            <Typography
              sx={{
                color: "#fff",
                fontSize: size * 0.05,
                letterSpacing: 2,
                lineHeight: 1,
              }}>
              - - - - -
            </Typography>
          </>
        ) : (
          <Typography
            sx={{
              color: "#fff",
              fontWeight: 700,
              fontSize: size * 0.054,
              lineHeight: 1,
            }}>
            <img
              src={mode === "download" ? DownloadIcon : mode === "upload" ? UploadIcon : PingIcon}
              alt=""
            />{" "}
            {resolvedStatus}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default SpeedDialGauge;
